import { useMemo, useState } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { ArrowUpDown, ArrowUp, ArrowDown, Table as TableIcon } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../lib/shadcn/table'
import { cn } from '../../lib/shadcn/utils'
import {
  DAILY_GROSS_TARGET_ZAR,
  formatHours,
  formatInt,
  formatPct,
  formatRand,
  formatRandExact,
  verdict,
  verdictClass,
  type DriverRow,
  type Targets,
} from '../../utils/performance'

const LEFT_COLS = new Set(['driver', 'manager'])
const isRight = (id: string) => !LEFT_COLS.has(id)

// A missing figure is a muted no-value marker, never R 0 (B16).
function moneyCell(value: number | null | undefined, signed = false) {
  if (value === null || value === undefined) {
    return <span className="tabular-nums text-muted-foreground">{formatRand(null)}</span>
  }
  const cls = signed
    ? value > 0
      ? 'text-emerald-600 dark:text-emerald-400'
      : value < 0
        ? 'text-red-600 dark:text-red-400'
        : ''
    : ''
  return <span className={cn('tabular-nums', cls)}>{formatRand(value)}</span>
}

// Earnings per shift, coloured amber below the daily target and emerald at or
// above it, so underperformance is scannable.
function perShiftCell(value: number | null | undefined) {
  if (value === null || value === undefined) {
    return <span className="tabular-nums text-muted-foreground">{formatRand(null)}</span>
  }
  const cls =
    value >= DAILY_GROSS_TARGET_ZAR
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-amber-600 dark:text-amber-400'
  return <span className={cn('tabular-nums font-medium', cls)}>{formatRand(value)}</span>
}

// Coaching metric cell: coloured by verdict against its target, muted em dash
// when the value has not been measured (null).
function coachCell(
  value: number | null,
  format: (v: number | null) => string,
  target: number | undefined,
  direction: 'higher_better' | 'lower_better',
) {
  const cls = verdictClass(verdict(value, target, direction))
  return <span className={cn('tabular-nums font-medium', cls)}>{format(value)}</span>
}

export function DriverTable({
  rows,
  targets,
  loading,
}: {
  rows: DriverRow[]
  targets?: Targets | null
  loading: boolean
}) {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'driver_net', desc: true }])

  const columns = useMemo<ColumnDef<DriverRow>[]>(
    () => [
      {
        accessorKey: 'driver',
        header: 'Driver',
        cell: (c) => <span className="font-medium text-foreground">{c.getValue<string>()}</span>,
      },
      {
        accessorKey: 'manager',
        header: 'Manager',
        cell: (c) => <span className="text-muted-foreground">{c.getValue<string>()}</span>,
      },
      {
        accessorKey: 'shifts',
        header: 'Shifts',
        cell: (c) => <span className="tabular-nums">{formatInt(c.getValue<number>())}</span>,
      },
      {
        id: 'uber_gross',
        accessorFn: (r) => r.uber_gross ?? undefined,
        sortUndefined: 'last',
        header: 'Uber gross',
        cell: (c) => moneyCell(c.getValue<number | undefined>()),
      },
      {
        id: 'earnings_per_shift',
        accessorFn: (r) => r.earnings_per_shift ?? undefined,
        sortUndefined: 'last',
        header: 'Earnings / shift',
        cell: (c) => perShiftCell(c.getValue<number | undefined>()),
      },
      {
        id: 'avg_online_hours',
        accessorFn: (r) => r.avg_online_hours ?? undefined,
        sortUndefined: 'last',
        header: () => (
          <span title={targets?.online_hours.source ?? undefined}>Online h</span>
        ),
        cell: (c) =>
          coachCell(
            c.row.original.avg_online_hours,
            formatHours,
            targets?.online_hours.target,
            'higher_better',
          ),
      },
      {
        id: 'acceptance_rate',
        accessorFn: (r) => r.acceptance_rate ?? undefined,
        sortUndefined: 'last',
        header: () => (
          <span title={targets?.acceptance_rate.source ?? undefined}>Accept %</span>
        ),
        cell: (c) =>
          coachCell(
            c.row.original.acceptance_rate,
            formatPct,
            targets?.acceptance_rate.target,
            'higher_better',
          ),
      },
      {
        id: 'avg_fare',
        accessorFn: (r) => r.avg_fare ?? undefined,
        sortUndefined: 'last',
        header: () => <span title={targets?.avg_fare.source ?? undefined}>Avg fare</span>,
        cell: (c) =>
          coachCell(
            c.row.original.avg_fare,
            formatRandExact,
            // Information only since db/migrations/5814, so never coloured.
            undefined,
            'higher_better',
          ),
      },
      {
        id: 'driver_net',
        accessorFn: (r) => r.driver_net ?? undefined,
        sortUndefined: 'last',
        header: 'Driver net',
        cell: (c) => moneyCell(c.getValue<number | undefined>()),
      },
      {
        id: 'petrol_cost',
        accessorFn: (r) => r.petrol_cost ?? undefined,
        sortUndefined: 'last',
        header: 'Petrol',
        cell: (c) => moneyCell(c.getValue<number | undefined>()),
      },
      {
        id: 'fuel_saving',
        accessorFn: (r) => r.fuel_saving ?? undefined,
        sortUndefined: 'last',
        header: 'Fuel saving',
        cell: (c) => moneyCell(c.getValue<number | undefined>(), true),
      },
      {
        id: 'driver_extras',
        accessorFn: (r) => r.driver_extras ?? undefined,
        sortUndefined: 'last',
        header: 'Driver extras',
        cell: (c) => moneyCell(c.getValue<number | undefined>(), true),
      },
    ],
    [targets],
  )

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  })

  return (
    <div className="rounded-lg border border-border bg-card shadow-retool-sm">
      <div className="flex items-center gap-2 border-b border-border px-4 py-3">
        <TableIcon className="h-4 w-4 text-muted-foreground" />
        <h2 className="text-sm font-semibold text-foreground">Drivers</h2>
        <span className="ml-auto text-xs text-muted-foreground tabular-nums">
          {loading ? '…' : `${rows.length} driver${rows.length === 1 ? '' : 's'}`}
        </span>
      </div>
      <div className="max-h-[520px] overflow-auto">
        <Table>
          <TableHeader className="sticky top-0 z-10 bg-card">
            {table.getHeaderGroups().map((hg) => (
              <TableRow key={hg.id}>
                {hg.headers.map((header) => {
                  const right = isRight(header.column.id)
                  const sorted = header.column.getIsSorted()
                  return (
                    <TableHead
                      key={header.id}
                      className={cn(
                        'cursor-pointer select-none whitespace-nowrap',
                        right && 'text-right',
                      )}
                      onClick={header.column.getToggleSortingHandler()}
                    >
                      <span
                        className={cn(
                          'inline-flex items-center gap-1',
                          right && 'flex-row-reverse',
                        )}
                      >
                        {flexRender(header.column.columnDef.header, header.getContext())}
                        {sorted === 'asc' ? (
                          <ArrowUp className="h-3 w-3" />
                        ) : sorted === 'desc' ? (
                          <ArrowDown className="h-3 w-3" />
                        ) : (
                          <ArrowUpDown className="h-3 w-3 opacity-40" />
                        )}
                      </span>
                    </TableHead>
                  )
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  Loading…
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columns.length} className="h-24 text-center text-muted-foreground">
                  No drivers for the selected filters.
                </TableCell>
              </TableRow>
            ) : (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id}>
                  {row.getVisibleCells().map((cell) => {
                    const right = isRight(cell.column.id)
                    return (
                      <TableCell
                        key={cell.id}
                        className={cn('whitespace-nowrap', right && 'text-right')}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </TableCell>
                    )
                  })}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
    </div>
  )
}
